import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { selectOutletId } from '@store/auth/authSelectors';
import { patchUserOutlet } from '@store/auth/authSlice';
import React, { useEffect } from 'react';
import { supabase } from '@/renderer/services/supabase';

// Keeps the signed-in user's outlet fresh across PCs that stay signed in.
// `outlets` isn't synced through PowerSync (it's only referenced inside
// `with:` CTEs in powersync.yaml to build filters, which never populates a
// local table), and `currentUser.outlet` is otherwise just a one-time Redux
// snapshot taken at login — so without this, a PC that opens a new business
// day never reflects on other PCs until they log out and back in.
//
// Realtime-only: a PC that's offline when the day is opened elsewhere will
// only catch up once it reconnects.
export const OutletLiveSync: React.FC = () => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectOutletId);

  useEffect(() => {
    if (!outletId) return undefined;

    const channel = supabase
      .channel(`outlet-live-sync-${outletId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'outlets',
          filter: `id=eq.${outletId}`,
        },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          dispatch(
            patchUserOutlet({
              id: outletId,
              day_open: (row.day_opened_at as string | null) ?? null,
              name: row.name as string,
              address: row.address as string | null,
              tin_number: row.tin_number as string | null,
              phone: row.phone as string | null,
              receipt_footnote: row.receipt_footnote as string | null,
            }),
          );
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [dispatch, outletId]);

  return null;
};
